/**
 * リアルタイム対戦（T15）サービス層。
 *
 * 責務:
 *   - `ws-ticket` の取得（POST /games/:gameId/rooms/:roomId/ws-ticket）
 *   - WebSocket 接続確立と自動再接続
 *   - 送受信メッセージの薄いイベント購読機構
 *   - アプリケーションコマンドの送信（`signal` broadcast or `room_command`）
 *   - `realtime-state` スナップショット取得（再接続後の state 同期用）
 *
 * 本ゲームでは Hub 側に reducer を登録しないため、通常プレイ中の着手同期は
 * WS `signal` ブロードキャストで行う（サーバー権威ではないが両クライアントで
 * 同じ engine を走らせる対称設計）。`sendCommand()` は `signal` 経由で、
 * 将来 Reducer を導入した場合は `room_command` に切り替えられるよう抽象化する。
 *
 * 参考:
 *   - docs/20_API/API_WebSocket.md
 *   - docs/30_Features/SDK_Reference_Multiplayer.md
 *   - docs/35_Guides/08_Multiplayer.md
 */

import { getGameId, ymgFetch } from './sdk'
import {
  REALTIME_MESSAGE_TYPES,
  WS_CLOSE_CODES,
  WS_CONNECT_TIMEOUT_MS,
  WS_HEARTBEAT_INTERVAL_MS,
  WS_RECONNECT_BASE_DELAY_MS,
  WS_RECONNECT_MAX_ATTEMPTS,
  WS_RECONNECT_MAX_DELAY_MS,
  type RealtimeCommandEnvelope,
  type RealtimeCommandType,
  type RealtimeConnectionState,
  type RealtimeMessage,
} from '../config/realtime'

// ---------------------------------------------------------------------------
// ws-ticket
// ---------------------------------------------------------------------------

export interface WsTicketResult {
  ticket: string
  /** Unix ms。発行から 30 秒後。 */
  expiresAt: number
}

/**
 * Hub から WebSocket 接続用の一時チケットを発行する。
 *
 * エラー種別:
 *   - 401: トークンが無効 or 期限切れ
 *   - 403: ルームのメンバーでない
 *   - 404: ルームが存在しない
 *   - 410: ルーム期限切れ
 *
 * `.claude/rules/no-silent-fallback.md` に従い、エラーは呼び出し側に伝播する。
 */
export async function getWsTicket(roomId: string): Promise<WsTicketResult> {
  const gameId = getGameId()
  const res = await ymgFetch<{ ok: boolean; ticket: string; expiresAt: number }>(
    `/games/${encodeURIComponent(gameId)}/rooms/${encodeURIComponent(roomId)}/ws-ticket`,
    { method: 'POST' },
  )
  return { ticket: res.ticket, expiresAt: res.expiresAt }
}

/**
 * WS 接続 URL を構築する。
 *
 * Hub 仕様: `wss://api.yamanekogames.com/ws?token=<TICKET>&roomId=<ROOM_ID>`
 * Sandbox / Production は baseUrl の host 部を wss に差し替える。
 */
export function buildWsUrl(apiBaseUrl: string, ticket: string, roomId: string): string {
  const httpUrl = new URL(apiBaseUrl)
  const wsProtocol = httpUrl.protocol === 'https:' ? 'wss:' : 'ws:'
  const wsUrl = new URL(`${wsProtocol}//${httpUrl.host}/ws`)
  wsUrl.searchParams.set('token', ticket)
  wsUrl.searchParams.set('roomId', roomId)
  return wsUrl.toString()
}

// ---------------------------------------------------------------------------
// realtime-state
// ---------------------------------------------------------------------------

export interface RealtimeStateResponse {
  ok: boolean
  snapshot: unknown
  events: unknown[]
}

/**
 * Firestore に永続化されたルームの realtime-state を取得する（再接続後の再同期用）。
 *
 * 本ゲームでは reducer を登録していないため snapshot は通常空だが、
 * 将来サーバー権威に移行した際のフックとして実装しておく。
 */
export async function fetchRealtimeState(
  roomId: string,
  limit = 20,
): Promise<RealtimeStateResponse> {
  const gameId = getGameId()
  const qs = limit > 0 ? `?limit=${encodeURIComponent(String(limit))}` : ''
  return ymgFetch<RealtimeStateResponse>(
    `/games/${encodeURIComponent(gameId)}/rooms/${encodeURIComponent(roomId)}/realtime-state${qs}`,
    { method: 'GET' },
  )
}

// ---------------------------------------------------------------------------
// RealtimeClient
// ---------------------------------------------------------------------------

type MessageHandler<TPayload = unknown> = (msg: RealtimeMessage<TPayload>) => void
type ConnectionStateHandler = (state: RealtimeConnectionState, detail?: { code?: number; reason?: string; attempt?: number }) => void

export interface RealtimeClientOptions {
  roomId: string
  /** API ベース URL。ws-ticket / realtime-state の取得と WS URL 構築に使う。 */
  apiBaseUrl: string
}

/**
 * 薄い WebSocket ラッパー。
 *
 * - EventEmitter 的な subscribe/publish を提供
 * - 切断時は自動再接続（最大 WS_RECONNECT_MAX_ATTEMPTS 回、指数バックオフ）
 * - heartbeat: WS_HEARTBEAT_INTERVAL_MS ごとに `ping` を送信
 * - `disconnect()` で正常切断（close code 1000）
 *
 * エラーは握り潰さず、`getLastError()` と connection state 経由で UI に通知する。
 */
export class RealtimeClient {
  private readonly roomId: string
  private readonly apiBaseUrl: string
  private ws: WebSocket | null = null
  private connectionState: RealtimeConnectionState = 'idle'
  private reconnectAttempt = 0
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private stopped = false
  private lastError: Error | null = null
  /** 明示的な正常切断（ユーザー操作）なら true。再接続しない。 */
  private intentionalClose = false

  private messageHandlers: Map<string, Set<MessageHandler>> = new Map()
  private anyHandlers: Set<MessageHandler> = new Set()
  private stateHandlers: Set<ConnectionStateHandler> = new Set()

  constructor(options: RealtimeClientOptions) {
    this.roomId = options.roomId
    this.apiBaseUrl = options.apiBaseUrl
  }

  getConnectionState(): RealtimeConnectionState {
    return this.connectionState
  }

  getLastError(): Error | null {
    return this.lastError
  }

  /**
   * 接続を開始する。1 回失敗したら例外を throw し、呼び出し側で UI に表示する。
   * 接続成功後は `close` イベントで自動再接続を試みる（stopped でない限り）。
   */
  async connect(): Promise<void> {
    this.stopped = false
    this.intentionalClose = false
    this.reconnectAttempt = 0
    await this.doConnect()
  }

  private async doConnect(): Promise<void> {
    this.setState('connecting')
    const { ticket } = await getWsTicket(this.roomId)
    const url = buildWsUrl(this.apiBaseUrl, ticket, this.roomId)

    await new Promise<void>((resolve, reject) => {
      let settled = false
      let ws: WebSocket
      try {
        ws = new WebSocket(url)
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)))
        return
      }
      this.ws = ws

      const timeoutHandle = setTimeout(() => {
        if (!settled) {
          settled = true
          try { ws.close() } catch { /* noop */ }
          reject(new Error('WebSocket connect timeout'))
        }
      }, WS_CONNECT_TIMEOUT_MS)

      ws.onopen = () => {
        clearTimeout(timeoutHandle)
        if (settled) return
        settled = true
        this.onOpen()
        resolve()
      }

      ws.onmessage = (ev: MessageEvent) => this.onMessage(ev)

      ws.onerror = (_ev) => {
        // WebSocket API は Error オブジェクトを提供しない。state は close で決まる。
        this.lastError = new Error('WebSocket error')
      }

      ws.onclose = (ev: CloseEvent) => {
        clearTimeout(timeoutHandle)
        if (!settled) {
          settled = true
          reject(new Error(`WebSocket closed before open (code=${ev.code}, reason=${ev.reason})`))
          return
        }
        this.onClose(ev)
      }
    })
  }

  private onOpen(): void {
    this.reconnectAttempt = 0
    this.setState('connected')
    this.startHeartbeat()
  }

  private onMessage(ev: MessageEvent): void {
    const raw = typeof ev.data === 'string' ? ev.data : ''
    if (!raw) return
    let parsed: RealtimeMessage
    try {
      parsed = JSON.parse(raw) as RealtimeMessage
    } catch (err) {
      // 不正な JSON はログして捨てる（Hub が送信することは仕様上ない）。
      console.warn('[RealtimeClient] invalid JSON message:', err, raw)
      return
    }

    // `pong` は内部処理のみ（heartbeat 生存確認）。それ以外は購読者に配信する。
    if (parsed.type === REALTIME_MESSAGE_TYPES.Pong) {
      return
    }
    if (parsed.type === REALTIME_MESSAGE_TYPES.Error) {
      const p = parsed.payload as { code?: string; message?: string } | undefined
      this.lastError = new Error(`WS error: ${p?.code ?? 'UNKNOWN'}: ${p?.message ?? ''}`)
    }

    for (const handler of this.anyHandlers) {
      try {
        handler(parsed)
      } catch (err) {
        console.warn('[RealtimeClient] any-handler failed:', err)
      }
    }
    const handlers = this.messageHandlers.get(parsed.type)
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(parsed)
        } catch (err) {
          console.warn('[RealtimeClient] handler failed:', err)
        }
      }
    }
  }

  private onClose(ev: CloseEvent): void {
    this.stopHeartbeat()
    this.ws = null

    if (this.intentionalClose || this.stopped) {
      this.setState('closed', { code: ev.code, reason: ev.reason })
      return
    }

    // 再接続不可のコード（認証失敗、キック、ルーム喪失、重複接続）は error に落とす。
    // DuplicateConnection (4008) を再接続対象に含めると、別タブ/別デバイスから
    // 同一ルームに繋がっている間 ws-ticket POST を連射し続ける無限ループになる。
    if (
      ev.code === WS_CLOSE_CODES.Unauthorized ||
      ev.code === WS_CLOSE_CODES.Forbidden ||
      ev.code === WS_CLOSE_CODES.RoomNotFound ||
      ev.code === WS_CLOSE_CODES.Kicked ||
      ev.code === WS_CLOSE_CODES.DuplicateConnection
    ) {
      this.setState('error', { code: ev.code, reason: ev.reason })
      return
    }

    // 指数バックオフで再接続を試行
    this.reconnectAttempt += 1
    if (this.reconnectAttempt > WS_RECONNECT_MAX_ATTEMPTS) {
      this.setState('error', { code: ev.code, reason: 'reconnect attempts exhausted' })
      return
    }
    const delay = Math.min(
      WS_RECONNECT_BASE_DELAY_MS * 2 ** (this.reconnectAttempt - 1),
      WS_RECONNECT_MAX_DELAY_MS,
    )
    this.setState('reconnecting', {
      code: ev.code,
      reason: ev.reason,
      attempt: this.reconnectAttempt,
    })
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (this.stopped) return
      this.doConnect().catch((err) => {
        this.lastError = err instanceof Error ? err : new Error(String(err))
        // doConnect 内で ws.onclose 経由で再度 onClose が呼ばれるため、
        // 追加の再接続はここで行わない。ただし即失敗時は明示的に error に遷移。
        if (this.connectionState !== 'reconnecting') {
          this.setState('error', { reason: this.lastError.message })
        }
      })
    }, delay)
  }

  private startHeartbeat(): void {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ type: REALTIME_MESSAGE_TYPES.Ping }))
        } catch (err) {
          console.warn('[RealtimeClient] heartbeat send failed:', err)
        }
      }
    }, WS_HEARTBEAT_INTERVAL_MS)
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  private setState(
    state: RealtimeConnectionState,
    detail?: { code?: number; reason?: string; attempt?: number },
  ): void {
    if (this.connectionState === state) return
    this.connectionState = state
    for (const handler of this.stateHandlers) {
      try {
        handler(state, detail)
      } catch (err) {
        console.warn('[RealtimeClient] state-handler failed:', err)
      }
    }
  }

  /**
   * アプリケーションコマンドをルーム全体に broadcast する。
   *
   * 本ゲームでは Hub に reducer を登録していないため `signal` メッセージを使う。
   * 将来サーバー権威に移行する場合は `room_command` に切り替える（同じ API）。
   */
  sendCommand<TPayload>(
    commandType: RealtimeCommandType,
    payload: TPayload,
  ): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('RealtimeClient: not connected')
    }
    const envelope: RealtimeCommandEnvelope<TPayload> = {
      commandType,
      payload,
      requestId: generateRequestId(),
    }
    this.ws.send(
      JSON.stringify({
        type: REALTIME_MESSAGE_TYPES.Signal,
        payload: envelope,
      }),
    )
  }

  /**
   * 特定ユーザーに直接メッセージを送る（ユニキャスト）。
   * RequestState / StateSnapshot など、再接続時の個別応答に使う。
   */
  sendTo<TPayload>(
    toUserId: string,
    commandType: RealtimeCommandType,
    payload: TPayload,
  ): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('RealtimeClient: not connected')
    }
    const envelope: RealtimeCommandEnvelope<TPayload> = {
      commandType,
      payload,
      requestId: generateRequestId(),
    }
    this.ws.send(
      JSON.stringify({
        type: REALTIME_MESSAGE_TYPES.Signal,
        to: toUserId,
        payload: envelope,
      }),
    )
  }

  /** メッセージタイプ単位で購読する。返り値を呼ぶと解除できる。 */
  on(type: string, handler: MessageHandler): () => void {
    let set = this.messageHandlers.get(type)
    if (!set) {
      set = new Set()
      this.messageHandlers.set(type, set)
    }
    set.add(handler)
    return () => set?.delete(handler)
  }

  /** 全メッセージを購読する。デバッグや横断ロギング用。 */
  onAny(handler: MessageHandler): () => void {
    this.anyHandlers.add(handler)
    return () => this.anyHandlers.delete(handler)
  }

  /** 接続状態の変化を購読する。 */
  onStateChange(handler: ConnectionStateHandler): () => void {
    this.stateHandlers.add(handler)
    return () => this.stateHandlers.delete(handler)
  }

  /**
   * 明示的に切断する。再接続は行わない。
   * アンマウント時 / 退出ボタン時に呼ぶ。
   */
  disconnect(): void {
    this.stopped = true
    this.intentionalClose = true
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.stopHeartbeat()
    if (this.ws) {
      try {
        this.ws.close(WS_CLOSE_CODES.Normal, 'client disconnect')
      } catch (err) {
        console.warn('[RealtimeClient] disconnect close failed:', err)
      }
      this.ws = null
    }
    this.setState('closed')
  }
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function generateRequestId(): string {
  // crypto.randomUUID は Android WebView 5.x 等の古い環境で未サポートなので、
  // 環境判定してフォールバック。衝突確率は対戦 1 セッション内では十分低い。
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `req_${crypto.randomUUID()}`
  }
  const rand = Math.random().toString(36).slice(2, 10)
  return `req_${Date.now().toString(36)}_${rand}`
}
